// statusLayers.ts
import * as L from "leaflet";
import "leaflet.markercluster";
import { StationStatus } from "../../domain/types";
import { markerToStation, stationToMarker } from "../markers/createMarker";
import { markDirty } from "../markers/refresh";
import { createClusterGroup } from "../cluster";
import { getStationById } from "../../domain/storage/stationsStore";
import { getLastUpdatedSampleTimestamp } from "../data/latestData";
import { getStationStatus } from "../markers/circleIcon";

let mgr: ReturnType<typeof createStatusLayerManager> | null = null;

export function initStatusManager(map: L.Map, useClusters = true) {
    if (!mgr) mgr = createStatusLayerManager(map, { useClusters });
    return mgr;
}

export function getStatusManager() {
    if (!mgr) throw new Error("Status manager not initialized. Call initStatusManager(map) first.");
    return mgr;
}

export function createStatusLayerManager(
    map: L.Map,
    opts: { useClusters?: boolean} = {}
) {
    const useClusters = opts.useClusters && (L as any).markerClusterGroup;
    const makeGroup = () => {
        const group: L.LayerGroup = useClusters ? (createClusterGroup() as L.LayerGroup) : L.layerGroup();

        const refreshVisible = () => {
            group.eachLayer(layer => {
                if (layer instanceof L.Marker && map.getBounds().contains(layer.getLatLng())) {
                    const sId = markerToStation.get(layer);
                    if (sId) markDirty(sId);
                }
            });
        };

        group.on("spiderfied", (e: any) => {
            e.cluster.getAllChildMarkers().forEach((m: L.Marker) => {
                const sId = markerToStation.get(m);
                if (sId) markDirty(sId);
            });
        });

        // group.on("animationend", refreshVisible);
        // map.on("zoomend", refreshVisible);
        // map.on("drag", refreshVisible);

        return group;
    };

    const groups: Record<StationStatus, L.LayerGroup> = {
        fresh: makeGroup(),
        stale: makeGroup(),
        offline: makeGroup(),
    };
    const clusters: L.LayerGroup<L.MarkerClusterGroup>[] = [groups.fresh, groups.stale, groups.offline];

    // start with all enabled
    (Object.values(groups) as L.LayerGroup[]).forEach(g => g.addTo(map));

    const enabled: Record<StationStatus, boolean> = { fresh: true, stale: true, offline: true };
    const index = new Map<string, { marker: L.Marker; status: StationStatus }>();

    const listeners = new Set<() => void>();
    const notify = () => listeners.forEach(fn => fn());

    function recomputeCounts() {
        const counts: Record<StationStatus, number> = { fresh: 0, stale: 0, offline: 0 };
        for (const { status } of index.values()) counts[status]++;
        return counts;
    }

    function add(id: string, marker: L.Marker, status: StationStatus): boolean {
        if (!marker) return false; // never add null markers
        const existing = index.get(id);
        if (existing) {
            if (existing.marker !== marker) {
                // move to the new marker instance if recreated
                groups[existing.status].removeLayer(existing.marker);
                existing.marker = marker;
                groups[existing.status].addLayer(existing.marker);
            }
            if (existing.status === status) {
                notify();
                return false;
            }
            groups[existing.status].removeLayer(existing.marker);
            existing.status = status;
            groups[status].addLayer(existing.marker);
            notify();
            return true;
        }

        index.set(id, { marker, status });
        groups[status].addLayer(marker);
        notify();
        return true;
    }

    function update(id: string, newStatus: StationStatus): boolean {
        const rec = index.get(id);
        if (!rec) {
            // try to resolve the marker created elsewhere
            const marker = stationToMarker.get(id);
            if (!marker) {
                return false; // marker not ready yet
            }
            return add(id, marker, newStatus);
        }
        if (rec.status === newStatus) {
            return false;
        }
        groups[rec.status].removeLayer(rec.marker);
        rec.status = newStatus;
        groups[newStatus].addLayer(rec.marker);
        notify();
        return true;
    }

    function getStatus(id: string): StationStatus {
        return index.get(id)?.status;
    }

    function remove(id: string) {
        const rec = index.get(id);
        if (!rec) return;
        groups[rec.status].removeLayer(rec.marker);
        index.delete(id);
        notify();
    }

    function setEnabled(status: StationStatus, on: boolean) {
        if (enabled[status] === on) return;
        enabled[status] = on;
        if (on) groups[status].addTo(map);
        else map.removeLayer(groups[status]);
        notify();
    }

    function getState() {
        return { enabled: { ...enabled }, counts: recomputeCounts() };
    }

    function subscribe(fn: () => void) {
        listeners.add(fn);
        return () => listeners.delete(fn);
    }

    function reevalAll() {
        const changed: string[] = [];
        for (const [id, rec] of index) {
            const s = getStationById(id);
            if (!s) continue;
            const ts = getLastUpdatedSampleTimestamp(s).getTime();
            const next = getStationStatus(ts);
            if (rec.status !== next) {
                update(id, next);
                changed.push(id);
            }
        }
        if (changed.length) changed.forEach(markDirty);
    }

    const ageTimer = window.setInterval(reevalAll, 10_000);
    const onVis = () => { if (!document.hidden) reevalAll(); };
    document.addEventListener("visibilitychange", onVis);

    function dispose() {
        clearInterval(ageTimer);
        document.removeEventListener("visibilitychange", onVis);
    }

    return { groups, add, update, remove, setEnabled, getState, subscribe, reevalAll, dispose, clusters, getStatus };
}
